import { useRef } from 'react';
import { toast } from 'sonner';
import { CalendarIcon, CopyIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/utils';
import type { CalendarFeedDto } from '@/lib/api/calendar';

import {
  googleCalendarUrl,
  outlookCalendarUrl,
  webcalUrl,
  withEmployeeParam,
} from './helpers';

interface Props {
  feed: CalendarFeedDto;
  /** Owner feeds only — scopes the feed to one employee before display. */
  employeeId?: string;
  /** Human label for the subscription, used by Outlook's "add from web". */
  feedName: string;
}

// Rendered once, right after `POST .../calendar/feed` succeeds — the token is
// never retrievable again. The URL is capability-bearing, so it's never logged.
export function CalendarFeedLinks({ feed, employeeId, feedName }: Props) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const displayUrl = withEmployeeParam(feed.url, employeeId);

  async function handleCopy() {
    const ok = await copyText(displayUrl);
    if (ok) {
      toast.success(t.calendarLinkCopied);
    } else {
      inputRef.current?.select();
      toast.error(t.calendarLinkCopyFailed);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={displayUrl}
          onFocus={(e) => e.currentTarget.select()}
          data-testid="calendar-feed-url"
          className="flex-1 min-w-0 px-3 py-2 text-sm border rounded-md bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCopy}
          data-testid="calendar-feed-copy"
        >
          <CopyIcon className="w-3.5 h-3.5" />
          {t.calendarCopy}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          asChild
          size="sm"
          variant="outline"
          data-testid="calendar-add-google"
        >
          <a href={googleCalendarUrl(displayUrl)} target="_blank" rel="noreferrer">
            <CalendarIcon className="w-3.5 h-3.5" />
            {t.calendarAddGoogle}
          </a>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          data-testid="calendar-add-apple"
        >
          <a href={webcalUrl(displayUrl)}>
            <CalendarIcon className="w-3.5 h-3.5" />
            {t.calendarAddApple}
          </a>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          data-testid="calendar-add-outlook"
        >
          <a
            href={outlookCalendarUrl(displayUrl, feedName)}
            target="_blank"
            rel="noreferrer"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            {t.calendarAddOutlook}
          </a>
        </Button>
      </div>
    </div>
  );
}
